import React from 'react'
import { Text, Loader, Button, Chip, ChipColor } from '@adc/ui-morpheus'
import { tasksAssignments } from '@adc/blueprint'
import type { DomainSummary } from '@adc/blueprint/lib/tasksAssignments/hooks'
import classnames from 'classnames/bind'
// @ts-ignore no types
import { Link, useHistory } from 'react-router-dom'
import {
  DOMAIN_CHIP_COLORS,
  encodeFilters,
  type UrgencyFilter,
  type WorkflowDomain,
} from '../../workQueuePage/types'
import styles from './styles.scss'

const cx = classnames.bind(styles)

/** Deep link to the work queue pre-filtered by urgency. */
function urgencyLink(u: UrgencyFilter): string {
  return `/work-queue?f=${encodeFilters({ u: [u] })}`
}

/** Deep link to the work queue pre-filtered by domain (type). */
function domainLink(d: WorkflowDomain): string {
  return `/work-queue?f=${encodeFilters({ t: [d] })}`
}

interface MyWorkSectionProps {
  partyId: string | null
}

/**
 * Summary card for the homepage. Shows overall progress, urgency pills, a
 * per-domain breakdown, and a button to open the full work queue.
 */
const MyWorkSection: React.FC<MyWorkSectionProps> = ({ partyId: _partyId }) => {
  const history = useHistory()
  const { summary, loading, error } =
    tasksAssignments.hooks.useWorkQueueSummary()

  if (loading) {
    return <Loader active inline='centered' size='small' inverted />
  }

  if (error) {
    return (
      <div className={cx('container')} data-elm-id='my_work_error'>
        <div className={cx('header')}>
          <Text.Header level={3} elmId='my_work_header'>
            My Work
          </Text.Header>
        </div>
        <div className={cx('center-state')}>
          <Text color='gray' size='small'>
            Unable to load assignments
          </Text>
        </div>
      </div>
    )
  }

  if (summary.totalWorkflows === 0) {
    return (
      <div className={cx('container')} data-elm-id='my_work_empty'>
        <div className={cx('header')}>
          <Text.Header level={3} elmId='my_work_header'>
            My Work
          </Text.Header>
        </div>
        <div className={cx('center-state')}>
          <Text color='gray' size='large'>
            No tasks assigned
          </Text>
          <Text color='gray' size='small' style={{ fontStyle: 'italic' }}>
            (Aren&apos;t you the fortunate one!)
          </Text>
        </div>
        <div className={cx('footer')}>
          <Link to='/work-queue'>
            <Button role='primary' size='small' elmId='my_work_open_full'>
              Open Full View
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const remaining =
    summary.total - summary.completed - summary.overdue - summary.dueToday

  return (
    <div className={cx('container')} data-elm-id='my_work_summary'>
      {/* Header */}
      <div className={cx('header')}>
        <Text.Header level={3} elmId='my_work_header'>
          My Work
        </Text.Header>
      </div>

      {/* Urgency chips — deep link to pre-filtered work queue */}
      <div className={cx('urgency-row')}>
        <Chip
          content={`${summary.overdue} Overdue`}
          color={ChipColor.Error}
          type='button'
          selectionStyle='light'
          size='small'
          fontWeight='bold'
          onClick={() => history.push(urgencyLink('OVERDUE'))}
          elmId='my_work_overdue_pill'
        />
        <Chip
          content={`${summary.dueToday} Due Today`}
          color={ChipColor.Caution}
          type='button'
          selectionStyle='light'
          size='small'
          fontWeight='bold'
          onClick={() => history.push(urgencyLink('DUE_TODAY'))}
          elmId='my_work_due_today_pill'
        />
        <Chip
          content={`${remaining > 0 ? remaining : summary.remaining} remaining`}
          color={ChipColor.Gray}
          type='button'
          selectionStyle='light'
          size='small'
          onClick={() => history.push(urgencyLink('REMAINING'))}
          elmId='my_work_remaining_pill'
        />
      </div>

      {/* Per-domain breakdown */}
      <div className={cx('domain-list')}>
        {(summary.domains ?? []).map((d: DomainSummary) => (
          <div key={d.domain} className={cx('domain-row')}>
            <Chip
              content={tasksAssignments.DOMAIN_LABELS[d.domain]}
              color={DOMAIN_CHIP_COLORS[d.domain]}
              type='button'
              selectionStyle='light'
              size='small'
              fontWeight='bold'
              onClick={() => history.push(domainLink(d.domain))}
              elmId={`my_work_domain_${d.domain.toLowerCase()}`}
            />
            <div className={cx('domain-stats')}>
              {d.overdue > 0 && (
                <span className={cx('stat', 'stat-error')}>
                  {d.overdue} overdue
                </span>
              )}
              <span
                className={cx('stat', {
                  'stat-active': d.inProgress > 0,
                  'stat-muted': d.inProgress === 0,
                })}
              >
                {d.inProgress} in progress
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className={cx('footer')}>
        <Link to='/work-queue'>
          <Button role='primary' size='small' elmId='my_work_view_all'>
            Open Full View
          </Button>
        </Link>
      </div>
    </div>
  )
}

export default MyWorkSection
